import React, { useCallback } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ErrorBoundary, ErrorTip, PageNotFound } from '@components/common';
import { useInitialization } from '@hooks';
import { trackErrorInEMP } from '@src/sensor';
import { ErrorConfig } from '../pages';
import { DefaultLayer } from '@components/common/Retry/Elements';
import ProviderWrapper from '@src/routers/Providers';
import { StyledAppWrapper, ContentWrapper } from '@src/routers/styled';
import { logger } from '@src/routers/constants';
import { NavApp, initNavStore, PRODUCTS, ENVIRONMENTS } from '@pulse/nav';
import 'antd/dist/reset.css';
import '@styles/index.less';
import ClipboardOnFocus from '@src/emp-3.0/components/Clipboard';

declare let BUILD_VARS: any;
logger.info(`build version: ${BUILD_VARS.VERSION}`);

interface IProps {
  initialCheck?: boolean;
  isWidget?: boolean;
  children: React.ReactNode;
}

const env = (BUILD_VARS.NODE_CONFIG_ENV || 'webappgcp') as ENVIRONMENTS;
initNavStore({ product: PRODUCTS.ACTIVATION, env });

const EmpInitCheck: React.FC<IProps> = ({ initialCheck, isWidget, children }) => {
  const [initialized, error, customerEnabled, username, isLoading] =
    useInitialization(initialCheck);
  if (initialCheck) {
    // Only show loading in Activation, for EMP in trends don't show the loading. HP-8047
    if (isLoading && !isWidget) {
      return (
        <React.Fragment key="loading-fragment">
          <DefaultLayer />
        </React.Fragment>
      );
    }
    if (!initialized && !isWidget) {
      return <PageNotFound />;
    }
    if (!customerEnabled) {
      logger.log('No EMP access');
      if (isWidget) {
        return null;
      }
      return <ErrorConfig />;
    }
    return <>{children}</>;
  }

  return <>{children}</>;
};

const BaseLayout: React.FC<IProps> = ({ children, initialCheck = true, isWidget }) => {
  const handleError = useCallback((error: Error) => {
    logger.error(error);
    const track = {
      errorMessage: error?.toString(),
      stack: error?.stack, //stack is a non-standard property for a stack trace.
      browserInfo: navigator.userAgent,
    };
    trackErrorInEMP(track);
  }, []);

  return (
    <StyledAppWrapper isComponentWithoutScroller={!window.__POWERED_BY_QIANKUN__} as={'div'}>
      <ErrorBoundary fallbackComponent={ErrorTip} onError={handleError}>
        <ProviderWrapper isWidget={isWidget}>
          <ClipboardOnFocus />
          {!isWidget && <NavApp />}
          <ContentWrapper>
            <EmpInitCheck initialCheck={initialCheck} isWidget={isWidget}>
              {isWidget ? (
                //emp in trends part should have BrowserRouter context for useNavigate
                <Routes>
                  <Route path="*" element={children} />
                </Routes>
              ) : (
                children
              )}
            </EmpInitCheck>
          </ContentWrapper>
        </ProviderWrapper>
      </ErrorBoundary>
    </StyledAppWrapper>
  );
};

export default BaseLayout;
